import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Show } from '@refinedev/antd';
import { useShow, useNavigation, useCan } from '@refinedev/core';
import { Tag, Image, Table, theme, Collapse, Button, Modal, message, Descriptions, Space } from 'antd';
import { Field } from '../../../components/Field';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';

const actionColors: Record<string, string> = {
  CREATE: '#52c41a',
  UPDATE: '#1890ff',
  LABEL_OCR_PROMOTE: '#722ed1',
  LABEL_OCR_PERSONAL: '#13c2c2',
  REVIEWED: '#fa8c16',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const NutritionTable: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const fields = [
    { key: 'calories', label: 'Calories', unit: 'kcal' },
    { key: 'protein', label: 'Protein', unit: 'g' },
    { key: 'fat', label: 'Fat', unit: 'g' },
    { key: 'carbs', label: 'Carbs', unit: 'g' },
    { key: 'fiber', label: 'Fiber', unit: 'g' },
    { key: 'sodium', label: 'Sodium', unit: 'mg' },
    { key: 'sugar', label: 'Sugar', unit: 'g' },
  ];
  const rows = fields.filter((f) => data[f.key] != null);
  if (rows.length === 0) return null;
  return (
    <Table
      dataSource={rows.map((f) => ({ field: f.label, value: data[f.key], unit: f.unit }))}
      rowKey="field"
      pagination={false}
      size="small"
    >
      <Table.Column dataIndex="field" title="Field" width={120} />
      <Table.Column dataIndex="value" title="Value" width={100} />
      <Table.Column dataIndex="unit" title="Unit" width={60} />
    </Table>
  );
};

export const TaggingLogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow({ resource: 'tagging-logs' });
  const { list } = useNavigation();
  const { data: canDelete } = useCan({ resource: 'tagging-logs', action: 'delete' });
  const [deleting, setDeleting] = useState(false);
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const changedFields = item.changedFields as Record<string, { before: unknown; after: unknown }> | null;
  const scanData = item.scanData as Record<string, unknown> | null;
  const labelData = item.labelData as Record<string, unknown> | null;
  const aiData = item.aiData as Record<string, unknown> | null;

  const handleDelete = async () => {
    Modal.confirm({
      title: 'Delete this log?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleting(true);
        try {
          await axiosInstance.delete(`${API_BASE_URL}/dish-catalog/tagging-logs/${item.id}`);
          message.success('Log deleted');
          list('tagging-logs');
        } catch {
          message.error('Failed to delete log');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  return (
    <Show resource="tagging-logs"
      headerButtons={() => (
        <Space>
          {canDelete?.can && (
            <Button danger loading={deleting} onClick={handleDelete}>Delete</Button>
          )}
        </Space>
      )}
    >
      <div style={{ padding: '8px 0' }}>
        <Field label="Action">
          <Tag style={tagStyle(actionColors[item.action] || '#898989')}>{item.action}</Tag>
        </Field>
        {item.dishCatalogId && (
          <Field label="Dish Catalog">
            <Link to={`/dish-catalog/show/${item.dishCatalogId}`}>{item.dishCatalogId}</Link>
          </Field>
        )}
        {(item.scanImageUrl || item.labelImageUrl) && (
          <Field label="Images">
            <div style={{ display: 'flex', gap: 12 }}>
              {item.scanImageUrl && <Image src={item.scanImageUrl} alt="Scan" width={120} height={120} style={{ borderRadius: 8, objectFit: 'cover' }} />}
              {item.labelImageUrl && <Image src={item.labelImageUrl} alt="Label" width={120} height={120} style={{ borderRadius: 8, objectFit: 'cover' }} />}
            </div>
          </Field>
        )}
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
      </div>

      {changedFields && Object.keys(changedFields).length > 0 && (
        <>
          <h3 style={{ color: token.colorText, margin: '24px 0 16px' }}>Changed Fields</h3>
          <Table
            dataSource={Object.entries(changedFields).map(([field, vals]) => ({ field, ...vals }))}
            rowKey="field"
            pagination={false}
            size="small"
          >
            <Table.Column dataIndex="field" title="Field" />
            <Table.Column dataIndex="before" title="Before" render={(v: unknown) => v != null ? (typeof v === 'object' ? JSON.stringify(v) : String(v)) : '—'} />
            <Table.Column dataIndex="after" title="After" render={(v: unknown) => v != null ? (typeof v === 'object' ? JSON.stringify(v) : String(v)) : '—'} />
          </Table>
        </>
      )}

      {/* Product Scan Response */}
      {scanData && (
        <Collapse style={{ marginTop: 24 }} items={[{
          key: 'scanData',
          label: 'Product Scan Result',
          children: (
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Name">{(scanData as any).aiSuggestion?.name || (scanData as any).name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Category">{(scanData as any).aiSuggestion?.category || (scanData as any).category || '—'}</Descriptions.Item>
              <Descriptions.Item label="Type">{(scanData as any).aiSuggestion?.type || (scanData as any).type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Portion">{(scanData as any).aiSuggestion?.standardPortionGrams || (scanData as any).standardPortionGrams || '—'}g</Descriptions.Item>
              {(scanData as any).aiSuggestion?.chineseName && (
                <Descriptions.Item label="Chinese Name">{(scanData as any).aiSuggestion.chineseName}</Descriptions.Item>
              )}
              {(scanData as any).aiSuggestion?.chineseNameTraditional && (
                <Descriptions.Item label="Chinese (Traditional)">{(scanData as any).aiSuggestion.chineseNameTraditional}</Descriptions.Item>
              )}
              {(scanData as any).aiSuggestion?.chineseNameSimplified && (
                <Descriptions.Item label="Chinese (Simplified)">{(scanData as any).aiSuggestion.chineseNameSimplified}</Descriptions.Item>
              )}
            </Descriptions>
          ),
        }]} />
      )}

      {/* Label Extraction Response */}
      {labelData && (
        <Collapse style={{ marginTop: 16 }} items={[{
          key: 'labelData',
          label: 'Label Extraction Result',
          children: (
            <>
              <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Product Name">{(labelData as any).productName || '—'}</Descriptions.Item>
                <Descriptions.Item label="Category">{(labelData as any).category || '—'}</Descriptions.Item>
                <Descriptions.Item label="Serving Size">{(labelData as any).servingSize || '—'}</Descriptions.Item>
                <Descriptions.Item label="Confidence">{(labelData as any).confidence != null ? `${(labelData as any).confidence * 100}%` : '—'}</Descriptions.Item>
                {(labelData as any).chineseName && (
                  <Descriptions.Item label="Chinese Name">{(labelData as any).chineseName}</Descriptions.Item>
                )}
                {(labelData as any).chineseNameTraditional && (
                  <Descriptions.Item label="Chinese (Traditional)">{(labelData as any).chineseNameTraditional}</Descriptions.Item>
                )}
                {(labelData as any).chineseNameSimplified && (
                  <Descriptions.Item label="Chinese (Simplified)">{(labelData as any).chineseNameSimplified}</Descriptions.Item>
                )}
              </Descriptions>
              <h4 style={{ margin: '12px 0 8px' }}>Nutrition</h4>
              <NutritionTable data={labelData as Record<string, unknown>} />
              {(labelData as any).ingredients && (
                <div style={{ marginTop: 12 }}>
                  <strong>Ingredients:</strong> <span style={{ color: token.colorTextSecondary }}>{(labelData as any).ingredients}</span>
                </div>
              )}
              {(labelData as any).allergens && (
                <div style={{ marginTop: 8 }}>
                  <strong>Allergens:</strong> <span style={{ color: '#ff4d4f' }}>{(labelData as any).allergens}</span>
                </div>
              )}
            </>
          ),
        }]} />
      )}

      {/* Raw JSON Responses */}
      <Collapse style={{ marginTop: 24 }} items={[
        ...(scanData ? [{
          key: 'rawScan',
          label: 'Raw Scan Response',
          children: (
            <pre style={{ background: token.colorBgContainerDisabled, padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 400 }}>
              {JSON.stringify(scanData, null, 2)}
            </pre>
          ),
        }] : []),
        ...(labelData ? [{
          key: 'rawLabel',
          label: 'Raw Label Response',
          children: (
            <pre style={{ background: token.colorBgContainerDisabled, padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 400 }}>
              {JSON.stringify(labelData, null, 2)}
            </pre>
          ),
        }] : []),
        ...(aiData && !scanData && !labelData ? [{
          key: 'aiData',
          label: 'AI Data',
          children: (
            <pre style={{ background: token.colorBgContainerDisabled, padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 400 }}>
              {JSON.stringify(aiData, null, 2)}
            </pre>
          ),
        }] : []),
      ].filter(Boolean)} />
    </Show>
  );
};
